import { CLASSES, type RunSummary } from '@xox/shared'
import { useEffect } from 'react'
import { Link } from 'react-router'
import { HistoryList } from '../components/HistoryList'
import { ProfileForm } from '../components/ProfileForm'
import { ProgressCard } from '../components/ProgressCard'
import { TransferPanel } from '../components/TransferPanel'
import { Card, Switch } from '../components/ui'
import { useFetch } from '../hooks/useFetch'
import { api } from '../lib/api'
import { useSession } from '../store/session'

/** Who you are here, how far you have come, and how to take it with you. */
export function Profile() {
  const me = useSession((s) => s.me)
  const load = useSession((s) => s.load)
  const setProfile = useSession((s) => s.setProfile)
  useEffect(() => {
    void load()
  }, [load])
  const { data: runs } = useFetch(api.myDungeonRuns)

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <h1 className="text-2xl font-black tracking-tight">Profile</h1>
      <Card className="flex flex-col gap-3">
        {me?.nicknameAuto && (
          <p className="text-sm text-slate-500">
            We called you <strong>{me.nickname}</strong> so you could start playing right away. Make
            it yours below.
          </p>
        )}
        <ProfileForm autoFocus={me?.nicknameAuto ?? false} />
      </Card>
      {me && <ProgressCard progress={me.progress} />}
      {me && (
        <Card className="flex items-center justify-between gap-4">
          <div>
            <div id="appear-offline-label" className="font-semibold">
              Appear offline
            </div>
            <p className="text-sm text-slate-500">
              You won't be listed as here, and challenges are off both ways.
            </p>
          </div>
          <Switch
            checked={me.appearOffline}
            onChange={(next) => void setProfile({ appearOffline: next })}
            aria-labelledby="appear-offline-label"
          />
        </Card>
      )}
      <TransferPanel />
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Dungeon runs</h2>
        {runs && runs.length === 0 && (
          <Card>
            <p className="text-slate-500">
              No runs yet.{' '}
              <Link to="/dungeon" className="font-semibold text-sky-600 hover:underline">
                Enter the dungeon.
              </Link>
            </p>
          </Card>
        )}
        <ul className="flex flex-col gap-2">
          {runs?.map((r) => (
            <RunRow key={r.id} run={r} />
          ))}
        </ul>
      </section>
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">My games</h2>
        <HistoryList />
      </section>
    </div>
  )
}

function RunRow({ run }: { run: RunSummary }) {
  const label =
    run.status === 'active'
      ? 'in progress'
      : run.status === 'won'
        ? 'cleared'
        : run.status === 'dead'
          ? 'died'
          : 'ended'
  return (
    <li>
      <Link to={`/dungeon/run/${run.id}`} className="block">
        <Card className="flex items-center justify-between gap-3 py-3">
          <div>
            <div className="font-medium">
              {run.kind === 'daily' ? `Daily ${run.day}` : 'Practice'}{' '}
              <span className="text-xs text-slate-500">· {CLASSES[run.cls].name}</span>
            </div>
            <div className="text-xs text-slate-500">
              Floor {run.floor} · {run.kills} kills · {label}
            </div>
          </div>
          <span className="text-lg font-black tabular-nums">{run.score}</span>
        </Card>
      </Link>
    </li>
  )
}
